import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next/';

import PreprUtil from '@utils/PreprUtil';
import {
  ADVERTISEMENT_DEVICE,
  ADVERTISEMENT_TYPE,
  IMAGE_SIZES,
  NavbarTabsTypes,
} from '@constants/enums';
import {
  DestinationLinksProvider,
  FooterLinksProvider,
  SiteLinksProvider,
  useLocale,
} from '@hooks/index';

import styles from './Archive.module.scss';

import { Meta } from '@components/layout/Meta/Meta';
import HighlightedTopList from '@components/lists/HighlightedTopList/HighlightedTopList';
import RecommendedTopList from '@components/lists/RecommendedTopList/RecommendedTopList';
import { PageProps } from '@constants/types';
import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import Layout from '@components/layout/Layout/Layout';
import { FB_APP_ID, XL_REVALIDATION_TIME } from '@constants/config';
import GenericService from '@services/GenericService';
import GraphqlService from '@services/GraphqlService';
import MailchimpService from '@services/MailchimpService';
import PersonalService from '@services/PersonalService';
import { GetStaticPropsContext } from 'next';
import { IManualTopListOverviewPublicationFields, TopListModule } from 'modules/TopListModule';
import { Advertisement } from '@components/layout/Advertisement/Advertisement';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<IManualTopListOverviewPublicationFields> {}

const ManualTopListOverview = ({ fields, footerLinks, destinationLinks, siteLinks }: Props) => {
  const locale = useLocale();
  const { t } = useTranslation();

  const pageTitle = fields.count
    ? `${t('DOMAIN.BUCKETLIST.THE_TOP')} ${fields.count.toLocaleString(locale).replace(',', '.')}`
    : t('DOMAIN.BUCKETLIST.THE_TOP');

  const getHighlightedListData = useCallback(() => {
    if (!fields.items) return [];
    return fields.items.map((item, idx) => {
      return PreprUtil.formatToListItemProps(
        item,
        undefined,
        undefined,
        idx === 0 ? IMAGE_SIZES.FULL : IMAGE_SIZES.MEDIUM,
      );
    });
  }, [fields.items]);

  const getTopListData = useCallback(() => {
    if (!fields.top_lists) return [];
    return fields.top_lists.map((item) => ({
      title: '',
      article: PreprUtil.TopListToProps(item, fields._slug),
      link: {
        label: t('DOMAIN.BUCKETLIST.SEE_ALL_X'),
        url: `/${fields._slug}/${item._slug}`,
      },
    }));
  }, [fields.top_lists]);

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout selectedTab={NavbarTabsTypes.TOPLISTS} paddings>
              <div className={styles.ManualTopListOverview}>
                <Meta
                  title={fields.meta_title || fields.title}
                  description={fields.meta_description || fields.title}
                />
                <div className="container">
                  <div>
                    <Advertisement
                      className={styles.Advertisement}
                      advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                      advertisementDevice={ADVERTISEMENT_DEVICE.MOBILE}
                    />
                  </div>
                  <HighlightedTopList
                    title={pageTitle}
                    articles={getHighlightedListData()}
                    link={{
                      label: t('DOMAIN.BUCKETLIST.SEE_FULL_LIST'),
                      url: `/${fields._slug}/${siteLinks?.top_list_all?.[0]._slug}`,
                    }}
                  />
                  <div className={styles.TopList}>
                    <RecommendedTopList topLists={getTopListData()} />
                  </div>
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default ManualTopListOverview;

export const getStaticProps = async ({ locale, preview }: GetStaticPropsContext) => {
  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const page = await TopListModule.getOverviewPage();

  if (!page) {
    return {
      notFound: true,
      revalidate: XL_REVALIDATION_TIME,
    };
  }

  return {
    props: page,
    revalidate: XL_REVALIDATION_TIME,
  };
};
